import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { Box, Divider, Flex, Text, useDisclosure } from '@chakra-ui/core'
import * as linkify from 'linkifyjs'
import {
  EditorContent,
  isEditorContentEmpty,
  richTextToPlainText,
} from 'components/CommonComponents/RichTextEditor'

import BaseModal from 'components/CommonComponents/BaseModal'
import { OptionType } from '../constants'
import { Switcher } from 'components/CommonComponents/Switcher'
import LocaleText from 'components/LocaleText'
import { RootState } from 'constants/interfaces'
import { determinePayloadGroups, determinePostContent, getPostType, isUserIncludedInGroups, options } from './utils'
import useAvatar from 'hooks/useAvatar'
import { AudienceSelectionBlock } from './AudienceSelectionBlock'
import { InputBlock } from './InputBlock'
import useLocaleText from 'components/useLocaleText'
import { MediaContentBlock } from './MediaContentBlock'
import { getLocalStorageItem, isValidDomain, updateLocalStorageItem } from 'utils'
import AudiencePublicationsModal from 'components/CommonComponents/AudiencePublicationModal'
import GroupsTree from 'components/CommonComponents/GroupsTree'
import { UsersList } from 'components/CommonComponents/UsersListModal'
import strings from 'constants/strings'
import { IHandleUpdatePost, createGroup, createPop, handleCreatePost, handleSchedulePost, handleUpdateScheduledPost, handleUpdatePost } from 'redux/actions/feed'
import { getTreeNodesIds } from 'components/CommonComponents/GroupsTree/utils'
import { PopInitiator, PopType } from 'components/PopOverview/utils'
import { setDummyPop } from 'redux/actions/pops'
import { AppDispatch } from 'config/redux'
import keys from 'constants/keys'
import { useSearchPage } from 'hooks/useSearchPage'
import { useMediaState } from '../../../hooks/useMediaState'
import { useAudienceState } from '../../../hooks/useAudienceState'
import { usePostTextContent } from './hooks/usePostTextContent'
import { LinkAttachmentModal } from './LinkAttachmentModal'
import PostModalFooter from './PostModalFooter'
import { TagsList } from 'components/CommonComponents/TagsListModal'
import { DropzoneWrapper } from 'components/DropzoneWrapper'
import { useDropzoneHandlers } from 'hooks/useDropzoneHandlers'
import CustomDatePicker from 'components/CommonComponents/CustomDatePicker'

interface PostModal {
  isOpen: boolean
  onClose: () => void
  files?: string[]
  videos?: string[]
  audios?: string[]
  groups?: string[]
  postTitle?: string
  postRichText?: EditorContent | null
  users?: string[]
  tags?: string[]
  images?: string[]
  postType?: number
  post_id?: string
  created_at_ts?: number
  ref_id?: string | null
  isChat?: boolean
  isEditing?: boolean
  admins?: string[]
  editJobId?: string
  defaultScheduledDate?: Date | null
}
export const PostModal = ({
  isOpen,
  onClose,
  files,
  videos,
  audios,
  groups,
  tags: tagIds,
  postTitle,
  postRichText,
  users,
  images,
  postType,
  post_id,
  created_at_ts,
  ref_id,
  admins,
  isChat = false,
  isEditing = false,
  editJobId,
  defaultScheduledDate,
}: PostModal) => {
  const dispatch: AppDispatch = useDispatch()
  const { isSearchPage } = useSearchPage()
  const {
    isOpen: isLinkAttachmentModalOpen,
    onOpen: onOpenLinkAttachmentModal,
    onClose: onCLoseLinkAttachmentModalOpen,
  } = useDisclosure()
  const { isOpen: isAudienceModalOpen, onOpen: onOpenAudienceModal, onClose: onCloseAudienceModal } = useDisclosure()
  const uid = useSelector((state: RootState) => state.auth.uid!)
  const isLoading = useSelector((state: RootState) => state.pops.isLoading)
  const { fetching } = useSelector((state: RootState) => state.feed)
  const {
    config: { default_group, retailUsersObject, first_name, last_name, user_role },
  } = useSelector((state: RootState) => state.config)
  const avatar = useAvatar(uid)

  const [selectedOptionType, setSelectedOptionType] = useState<string>(getPostType(postType))
  const [isSwitchPopActive, setIsSwitchPopActive] = useState(true)
  const [isSwitchAnnouncementActive, setIsSwitchAnnouncementActive] = useState(false)
  const [scheduledDate, setScheduledDate] = useState<Date | null>(
    editJobId ? (defaultScheduledDate ?? new Date()) : (defaultScheduledDate ?? null)
  )
  const [attachedLink, setAttachedLink] = useState(ref_id ?? null)
  const { richTextValue, setRichTextValue, title, setTitle, message, setMessage, clearTextContentState } =
    usePostTextContent({ postTitle, postRichText })

  const {
    media,
    uploadedFilesURL,
    uploadedImagesURL,
    uploadedVideosURL,
    audioRecords,
    handleFileUpload,
    handleMediaUploading,
    deleteFileHandler,
    removeAudioRecord,
    sendAudioRecord,
    isUploadLoading,
    handlerDeleteAllMedia,
    handleSingleMediaUploading,
    handleDeleteMedia,
    addMediaHandler,
    fileInputRef,
    imageInputRef,
    formFileRef,
    clearMediaState,
    setUploadedImagesURL,
    setUploadedVideosURL,
  } = useMediaState({ audios, files, images, videos })

  const {
    selectedUsersUids,
    setSelectedUsersUids,
    selectedGroupsIds,
    setSelectedGroupsIds,
    selectedTagsIds,
    setSelectedTagsIds,
    groupsToPayload,
    setGroupsToPayload,
    namesForTooltip,
    splittedGroupNames,
    filteredRerailUsersByTreeUsers,
    treeUsers,
    treeGroups,
    treeTags,
    clearAudienceState,
  } = useAudienceState({ users, groups, postType, tagIds, selectedOptionType })

  const t_create_post_placeholder = useLocaleText('create_post_placeholder')

  const validLinks = useMemo(() => {
    const plainText = richTextToPlainText(richTextValue)
    const foundLinks = linkify.find(plainText)
    return foundLinks.filter((link) => isValidDomain(link.href))
  }, [richTextValue])

  const clearState = useCallback(() => {
    clearMediaState()
    clearAudienceState()
    clearTextContentState()
    setAttachedLink(null)
    setIsSwitchPopActive(true)
    setIsSwitchAnnouncementActive(false)
    setSelectedOptionType(OptionType.POST)
    setScheduledDate(null)
  }, [clearAudienceState, clearMediaState, clearTextContentState])

  const closeModal = useCallback(() => {
    onClose()
    clearState()
  }, [clearState, onClose])

  const onTogglePopSwitch = useCallback(() => {
    setIsSwitchPopActive(!isSwitchPopActive)
  }, [isSwitchPopActive])

  const onToggleAnnouncementSwitch = useCallback(() => {
    setIsSwitchAnnouncementActive(!isSwitchAnnouncementActive)
  }, [isSwitchAnnouncementActive])

  const isDisabledPublishButton = () => {
    const hasNoRecipients = isEmpty(selectedGroupsIds) && isEmpty(selectedUsersUids) && isEmpty(selectedTagsIds)

    const isProcessing = isUploadLoading || fetching || isLoading

    if (hasNoRecipients || isProcessing) {
      return true
    }

    switch (selectedOptionType) {
      case OptionType.CHAT:
        const noContentChats = !isEditing ? isEmpty(message) || isEmpty(title) : isEmpty(title)

        return noContentChats

      case OptionType.POP:
        const noContentPops = isEmpty(message) || isEmpty(title)
        return noContentPops

      case OptionType.POST:
        const noContentPosts = !richTextValue || isEditorContentEmpty(richTextValue)
        const noMedia =
          isEmpty(uploadedImagesURL) && isEmpty(uploadedFilesURL) && isEmpty(audioRecords) && isEmpty(uploadedVideosURL)
        return noContentPosts && noMedia
      default:
        return false
    }
  }

  const renderViewOptionModal = useCallback(
    (viewOption: number, setViewOption: React.Dispatch<React.SetStateAction<number>> | any) => {
      switch (viewOption) {
        case 0:
          return (
            <GroupsTree
              isOpen={isOpen}
              selectedGroupID={selectedGroupsIds}
              mode={
                selectedOptionType === OptionType.CHAT || selectedOptionType === OptionType.POP
                  ? strings.PARTIAL_MULTILPE_MODE
                  : strings.MULTIPLE_MODE
              }
              setViewOption={setViewOption}
              setSelectedGroupID={setSelectedGroupsIds}
              setGroupsToPayload={setGroupsToPayload}
              groupsToPayload={groupsToPayload}
              isAllowSubmitWhithoutGroups={true}
              treeGroups={treeGroups}
            />
          )
        case 1:
          return (
            <UsersList
              isOpen={isOpen}
              onClose={onClose}
              setViewOption={setViewOption}
              selectedUsersUids={selectedUsersUids}
              setSelectedUsersUids={setSelectedUsersUids}
              treeUsers={filteredRerailUsersByTreeUsers}
            />
          )
        case 2:
          return (
            <TagsList
              isOpen={isOpen}
              onClose={onClose}
              setViewOption={setViewOption}
              tags={treeTags}
              selectedTagsIds={selectedTagsIds}
              setSelectedTagsIds={setSelectedTagsIds}
            />
          )

        default:
          break
      }
    },
    [
      filteredRerailUsersByTreeUsers,
      groupsToPayload,
      isOpen,
      onClose,
      selectedGroupsIds,
      selectedOptionType,
      selectedTagsIds,
      selectedUsersUids,
      setGroupsToPayload,
      setSelectedGroupsIds,
      setSelectedTagsIds,
      setSelectedUsersUids,
      treeTags,
      treeGroups,
    ]
  )

  const onCreatePost = useCallback(async () => {
    try {
      const res = await dispatch(
        handleCreatePost({
          rich_text: richTextValue,
          images: uploadedImagesURL,
          refId: attachedLink ? attachedLink : null,
          groups: groupsToPayload,
          users: selectedUsersUids,
          tags: selectedTagsIds,
          files: uploadedFilesURL,
          videos: uploadedVideosURL,
          audios: audioRecords,
        })
      )
      if (res) {
        closeModal()
        updateLocalStorageItem('defaultAudience', {
          [uid]: { groups: groupsToPayload, users: selectedUsersUids, tags: selectedTagsIds },
        })
      }
    } catch (e) {
      console.log(e)
    }
  }, [
    attachedLink,
    audioRecords,
    closeModal,
    dispatch,
    richTextValue,
    groupsToPayload,
    selectedTagsIds,
    selectedUsersUids,
    uid,
    uploadedFilesURL,
    uploadedImagesURL,
    uploadedVideosURL,
  ])

  const onSchedulePost = useCallback(async () => {
    if (!scheduledDate) return
    try {
      const sharedParams = {
        content: richTextToPlainText(richTextValue),
        rich_text: richTextValue,
        images: uploadedImagesURL,
        refId: attachedLink ? attachedLink : null,
        groups: groupsToPayload,
        users: selectedUsersUids,
        tags: selectedTagsIds,
        files: uploadedFilesURL,
        videos: uploadedVideosURL,
        audios: audioRecords,
        scheduledDate,
      }
      const res = editJobId
        ? await dispatch(handleUpdateScheduledPost({ ...sharedParams, editJobId }))
        : await dispatch(handleSchedulePost(sharedParams))
      if (res) {
        closeModal()
      }
    } catch (e) {
      console.log(e)
    }
  }, [
    attachedLink,
    audioRecords,
    closeModal,
    dispatch,
    editJobId,
    richTextValue,
    groupsToPayload,
    scheduledDate,
    selectedTagsIds,
    selectedUsersUids,
    uploadedFilesURL,
    uploadedImagesURL,
    uploadedVideosURL,
  ])

  const onCreatePop = async () => {
    try {
      const res = await dispatch(
        createPop({
          title,
          message,
          users: selectedUsersUids,
          groups: selectedGroupsIds,
          tags: selectedTagsIds,
          isPopPost: isSwitchPopActive,
          popImage: uploadedImagesURL?.[0],
          video: uploadedVideosURL?.[0],
        })
      )

      if (res) {
        onClose()
        const dummyPop = {
          pop_title: title,
          pop_body: message,
          profile_image_url: retailUsersObject?.[uid]?.profile_img_url || null,
          video: uploadedVideosURL?.[0] || null,
          pop_image_url: uploadedImagesURL?.[0] || null,
          ack_btn_text: 'newsfeed_pop_ack_btn',
          created_by: uid,
          created_at_ts: Math.floor(Date.now() / 1000),
          pop_type: PopType.DUMMY_POP,
          pop_initiator: PopInitiator.APP_USER,
        }
        setTimeout(() => {
          dispatch(setDummyPop(dummyPop))
        }, 3000)
      }
    } catch (e) {
      console.log(e)
    }
  }

  const onCreateGroup = async () => {
    try {
      const isCreatorInGroups = isUserIncludedInGroups(uid, selectedGroupsIds, treeGroups)
      const isCreatorInTags = checkIsCreatorInTags()
      const groupUsers =
        isCreatorInGroups || isCreatorInTags ? selectedUsersUids : Array.from(new Set([...selectedUsersUids, uid]))

      const res = await dispatch(
        createGroup({
          title: title,
          content: message,
          groups: selectedGroupsIds,
          users: groupUsers,
          tags: selectedTagsIds,
          images: uploadedImagesURL,
          type: isSwitchAnnouncementActive ? keys.POST_TYPE.GROUP_POST_ANNOUNCEMENT : keys.POST_TYPE.GROUP_POST,
        })
      )
      if (res) closeModal()
    } catch (e) {
      console.log(e)
    }
  }

  const onEditPost = async () => {
    if (postType === undefined || post_id === undefined || created_at_ts === undefined) return

    const newUsersToPublish = selectedUsersUids.filter((id) => !users?.includes(id))
    const usersToDelete = (users && users.filter((user: string) => !selectedUsersUids.includes(user))) || []
    const newGroupsToPublish = (
      postType !== keys.POST_TYPE.GROUP_POST ? groupsToPayload : selectedGroupsIds ?? []
    ).filter((id) => !groups?.includes(id))
    const groupsToDelete = groups?.filter(
      (id: string) => !(postType !== keys.POST_TYPE.GROUP_POST ? groupsToPayload : selectedGroupsIds ?? []).includes(id)
    )
    const newTagsToPublish = selectedTagsIds.filter((id) => !tagIds?.includes(id))
    const tagsToDelete = (tagIds && tagIds.filter((tagId: string) => !selectedTagsIds.includes(tagId))) || []
    const isCreatorInGroups = isUserIncludedInGroups(uid, groupsToPayload, treeGroups)
    const isCreatorInTags = checkIsCreatorInTags()
    const postUsers =
      isCreatorInGroups || isCreatorInTags ? selectedUsersUids : Array.from(new Set([...selectedUsersUids, uid]))

    const postContent = determinePostContent(postType, title, richTextToPlainText(richTextValue))
    const payloadGroups = determinePayloadGroups(postType, selectedGroupsIds, groupsToPayload)

    const postData: IHandleUpdatePost = {
      postType,
      title: postContent,
      rich_text: richTextValue,
      images: uploadedImagesURL,
      postId: post_id,
      taskRefId: attachedLink,
      groups,
      users: postUsers,
      tags: selectedTagsIds,
      newUsersToPublish,
      usersToDelete,
      newGroupsToPublish,
      groupsToDelete,
      newTagsToPublish,
      tagsToDelete,
      createdAtTs: created_at_ts,
      files: uploadedFilesURL,
      audios: audioRecords,
      videos: uploadedVideosURL,
      isSearchPage,
      groupsToPayload: payloadGroups,
      isChat,
      admins: admins,
    }

    try {
      const res = await dispatch(handleUpdatePost(postData))
      if (res) {
        onClose()
        clearState()
      }
    } catch (e) {
      console.log('Failed to create post:', e)
    }
  }
  const checkIsCreatorInTags = () => {
    if (!selectedTagsIds.length) {
      return false
    }
    const selectedTags = treeTags.filter((tag) => selectedTagsIds.includes(tag.sk))
    const mergedTagsGroups = Array.from(new Set(selectedTags.map((tag) => tag?.groups ?? []).flat()))

    const mergedTagsUsers = Array.from(new Set(selectedTags.map((tag) => tag?.users ?? []).flat()))
    return isUserIncludedInGroups(uid, mergedTagsGroups, treeGroups) || mergedTagsUsers.includes(uid)
  }

  const submitActionHandler = () => {
    if (isEditing) {
      onEditPost()
      return
    }

    if (editJobId && selectedOptionType === OptionType.POST) {
      onSchedulePost()
      return
    }

    const actionMap = {
      [OptionType.POST]: scheduledDate ? onSchedulePost : onCreatePost,
      [OptionType.POP]: onCreatePop,
      [OptionType.CHAT]: onCreateGroup,
    }

    const action = actionMap[selectedOptionType]
    if (action) {
      action()
    }
  }
  const onOptionChange = (optionType: string) => {
    setSelectedOptionType(optionType)
    if (optionType === OptionType.POP) {
      setSelectedTagsIds([])
    }
  }

  const handleUploadMedia = async (files: File[]) => {
    if (!files.length) return

    switch (selectedOptionType) {
      case OptionType.POST:
        const imageVideos: File[] = []
        const otherFiles: File[] = []

        files.forEach((file) => {
          const fileType = file.type
          if (fileType.startsWith('image') || fileType.startsWith('video')) {
            imageVideos.push(file)
          } else {
            otherFiles.push(file)
          }
        })

        const uploadPromises = []

        if (imageVideos.length > 0) {
          const event = {
            target: {
              files: imageVideos as unknown as FileList,
            },
          } as unknown as React.ChangeEvent<HTMLInputElement>
          uploadPromises.push(handleMediaUploading(event))
        }

        if (otherFiles.length > 0) {
          const event = {
            currentTarget: {
              files: otherFiles as unknown as FileList,
            },
          } as unknown as React.ChangeEvent<HTMLInputElement>
          uploadPromises.push(handleFileUpload(event))
        }

        await Promise.all(uploadPromises)
        break

      case OptionType.POP:
        const imageVideoFile = files.filter((file) => file.type.startsWith('image') || file.type.startsWith('video'))
        if (imageVideoFile.length > 0) {
          const event = {
            target: {
              files: [imageVideoFile[0]] as unknown as FileList,
            },
          } as unknown as React.ChangeEvent<HTMLInputElement>
          setUploadedImagesURL([])
          setUploadedVideosURL([])
          await handleMediaUploading(event)
        }
        break

      case OptionType.CHAT:
        break
    }
  }

  const { onDrop, handlePaste } = useDropzoneHandlers({
    onUploadMedia: handleUploadMedia,
    accept: `${strings.POST_FILE_ACCEPT_EXTENSIONS},image/*,video/*`,
  })

  useEffect(() => {
    if (
      !isAudienceModalOpen &&
      !selectedGroupsIds.length &&
      !selectedUsersUids.length &&
      !selectedTagsIds.length &&
      !groupsToPayload.length &&
      !isEditing
    ) {
      const defaultAudience: object | null = getLocalStorageItem('defaultAudience')
      let hasProcessed = false

      if (defaultAudience && defaultAudience[uid] && treeGroups && !isEmpty(treeGroups)) {
        const { users, groups }: { users: string[]; groups: string[] } = defaultAudience[uid]
        const matchingGroups = groups.filter((group) => Object.keys(treeGroups).includes(group))
        const matchingUsers = users.filter((user) => Object.keys(retailUsersObject!).includes(user))

        if (matchingUsers.length) {
          setSelectedUsersUids(users.filter((uid) => Object.keys(retailUsersObject!).includes(uid)))
          hasProcessed = true
        }
        if (matchingGroups.length) {
          const treeNodesIds = groups.flatMap((group) => getTreeNodesIds(treeGroups, group))
          setGroupsToPayload(groups)
          setSelectedGroupsIds(treeNodesIds)
          hasProcessed = true
        }
      }

      if (!hasProcessed && default_group !== null && treeGroups && !isEmpty(treeGroups)) {
        setGroupsToPayload([default_group])
        setSelectedGroupsIds(getTreeNodesIds(treeGroups, default_group))
      }
    }
  }, [
    default_group,
    groupsToPayload.length,
    isAudienceModalOpen,
    isEditing,
    retailUsersObject,
    selectedGroupsIds.length,
    selectedTagsIds.length,
    selectedUsersUids.length,
    setGroupsToPayload,
    setSelectedGroupsIds,
    setSelectedUsersUids,
    treeGroups,
    uid,
  ])

  return (
    <BaseModal
      display={isLinkAttachmentModalOpen || isAudienceModalOpen ? 'none' : 'block'}
      isShowAnimation={true}
      isOpen={isOpen}
      onClose={closeModal}
      hideCloseButton={isAudienceModalOpen}
      headerContent={
        <>
          <Text mb="10px">
            <LocaleText text={!isEditing ? 'create_new_pop_post' : 'edit'} />
          </Text>
          <Divider />
          <AudienceSelectionBlock
            isEditing={isEditing}
            avatar={avatar}
            first_name={first_name}
            last_name={last_name}
            user_role={user_role}
            splittedGroupNames={splittedGroupNames}
            namesForTooltip={namesForTooltip}
            selectedOptionType={selectedOptionType}
            isSwitchPopActive={isSwitchPopActive}
            onTogglePopSwitch={onTogglePopSwitch}
            isSwitchAnnouncementActive={isSwitchAnnouncementActive}
            onToggleAnnouncementSwitch={onToggleAnnouncementSwitch}
            onOpenAudienceModal={onOpenAudienceModal}
            uploadedImagesURL={uploadedImagesURL}
            imageInputRef={imageInputRef}
            handleSingleMediaUploading={handleSingleMediaUploading}
            isScheduleEnabled={!!scheduledDate}
            onToggleSchedule={() => setScheduledDate(scheduledDate ? null : new Date())}
            isScheduleLocked={!!editJobId}
          />
          {selectedOptionType === OptionType.POST && !isEditing && scheduledDate && (
            <Flex mt="8px">
              <CustomDatePicker
                selected={scheduledDate}
                onChange={(date) => setScheduledDate(date as Date | null)}
                showTimeInput
                minDate={new Date()}
                dateFormat="MM/dd/yyyy HH:mm"
                placeholderText="select_date"
              />
            </Flex>
          )}
        </>
      }
      bodyContent={
        <Box px="6" mt={0}>
          <Divider mb="20px" />
          {!isEditing && (
            <Box mx="40px">
              <Switcher options={options} onOptionChange={onOptionChange} selectedOption={selectedOptionType} />
            </Box>
          )}
          <Divider my="20px" />
          <DropzoneWrapper
            alignItems="flex-start"
            minH="200px"
            onDrop={onDrop}
            onPaste={handlePaste}
            disabled={selectedOptionType === OptionType.CHAT}
          >
            <InputBlock
              selectedOptionType={selectedOptionType}
              richTextValue={richTextValue}
              setRichTextValue={setRichTextValue}
              setTitle={setTitle}
              title={title}
              placeholder={t_create_post_placeholder}
              message={message}
              setMessage={setMessage}
              postType={postType}
              isEditing={isEditing}
            />

            <MediaContentBlock
              validLinks={validLinks}
              audioRecords={audioRecords}
              uploadedFilesURL={uploadedFilesURL}
              uploadedImagesURL={uploadedImagesURL}
              uploadedVideosURL={uploadedVideosURL}
              media={media}
              addMediaHandler={addMediaHandler}
              handlerDeleteAllMedia={handlerDeleteAllMedia}
              handleDeleteMedia={handleDeleteMedia}
              removeAudioRecord={removeAudioRecord}
              deleteFileHandler={deleteFileHandler}
              selectedOptionType={selectedOptionType}
            />
          </DropzoneWrapper>

          {isAudienceModalOpen && (
            <AudiencePublicationsModal
              isOpen={isAudienceModalOpen}
              groups={treeGroups}
              users={treeUsers}
              onClose={onCloseAudienceModal}
              renderViewOptionModal={renderViewOptionModal}
              selectedUsersUids={selectedUsersUids}
              selectedOptionType={selectedOptionType}
              selectedTagsIds={selectedTagsIds}
              groupsToPayload={
                selectedOptionType === OptionType.CHAT || selectedOptionType === OptionType.POP
                  ? selectedGroupsIds
                  : groupsToPayload ?? []
              }
            />
          )}

          {isLinkAttachmentModalOpen && (
            <LinkAttachmentModal
              isOpen={isLinkAttachmentModalOpen}
              closeModal={onCLoseLinkAttachmentModalOpen}
              setAttachedLink={setAttachedLink}
              attachedLink={attachedLink}
            />
          )}
        </Box>
      }
      footerContent={
        <>
          {!isAudienceModalOpen && (
            <PostModalFooter
              selectedOptionType={selectedOptionType}
              fileInputRef={fileInputRef}
              imageInputRef={imageInputRef}
              handleMediaUpload={handleMediaUploading}
              attachedLink={attachedLink}
              isDisabledPublishButton={isDisabledPublishButton()}
              handleFileUpload={handleFileUpload}
              formFileRef={formFileRef}
              onSuccessAudioRecording={sendAudioRecord}
              submitActionHandler={submitActionHandler}
              onOpenLinkAttachmentModal={onOpenLinkAttachmentModal}
              setUploadedImagesURL={setUploadedImagesURL}
              setUploadedVideosURL={setUploadedVideosURL}
              scheduledDate={scheduledDate}
            />
          )}
        </>
      }
    />
  )
}
